/*
 * Client-side validation for the slimmed product-config form (U3).
 *
 * Parity with the backend validators — these mirror the server so a malformed
 * config is caught inline before the round-trip, but the server remains the
 * authority (its 422/409 still surface via an Alert):
 *
 *   - `account_id` must be bare digits (KTD-7: the server prepends `act_` on write).
 *   - `ad_naming_column` is a 1–2 letter spreadsheet column (case-insensitive
 *     input, uppercased on serialize — KTD-5).
 *   - `geo_country`, when present, is a 2-letter ISO-3166 alpha-2 code (uppercased).
 */

import type { Schemas } from "../../api/client";

export type ProductConfigPayload = Schemas["ProductConfigPayload"];

export interface FieldErrors {
  /** Top-level scalar field errors, keyed by field name. */
  fields: Record<string, string>;
  /** True when nothing above is populated. */
  ok: boolean;
}

const EMPTY_ERRORS = (): FieldErrors => ({ fields: {}, ok: true });

/**
 * KTD-7: the user enters a BARE numeric account id (e.g. `1234567890`); the
 * server prepends `act_` on write. Accept only digits — reject anything
 * already `act_`-prefixed or containing non-digit characters.
 */
export function isValidAccountId(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

/** A 1–2 letter spreadsheet column (case-insensitive). KTD-5. */
export function isValidAdNamingColumn(value: string): boolean {
  return /^[A-Za-z]{1,2}$/.test(value.trim());
}

/** A 2-letter ISO-3166 alpha-2 country code (case-insensitive). */
export function isValidGeoCountry(value: string): boolean {
  return /^[A-Za-z]{2}$/.test(value.trim());
}

/** Prefill-v2 default landing URL — https only (mirrors the server @Matches
 *  and the wizard's own URL gate, so a saved default is always usable). */
export function isValidDefaultLink(value: string): boolean {
  return /^https:\/\/\S+$/.test(value.trim());
}

/** Prefill-v2 default page id — bare digits (mirrors RunCreate.page_id). */
export function isValidDefaultPageId(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

/** Display currency symbol/code — 1–3 non-whitespace chars (mirrors the server). */
export function isValidCurrency(value: string): boolean {
  return /^\S{1,3}$/.test(value.trim());
}

interface DraftFields {
  account_id: string;
  ad_naming_column: string;
  currency: string;
  geo_country: string;
  default_link: string;
  default_page_id: string;
}

/** Validate the editable draft's scalar fields. */
export function validateDraft(draft: DraftFields): FieldErrors {
  const errors = EMPTY_ERRORS();

  if (draft.account_id.trim() && !isValidAccountId(draft.account_id)) {
    errors.fields.account_id =
      "Enter digits only — do not include `act_` (e.g. 1234567890).";
  }

  // The AD_NAMING column is required and must be 1–2 letters (e.g. "V").
  if (!isValidAdNamingColumn(draft.ad_naming_column)) {
    errors.fields.ad_naming_column =
      "Enter a column letter, e.g. V (1–2 letters).";
  }

  if (draft.currency.trim() && !isValidCurrency(draft.currency)) {
    errors.fields.currency =
      "Enter a short symbol or code, e.g. £ (1–3 characters), or leave blank for $.";
  }

  if (draft.geo_country.trim() && !isValidGeoCountry(draft.geo_country)) {
    errors.fields.geo_country =
      "Enter a 2-letter country code, e.g. US.";
  }

  if (draft.default_link.trim() && !isValidDefaultLink(draft.default_link)) {
    errors.fields.default_link =
      "Enter a full https:// URL (the wizard rejects anything else).";
  }

  if (draft.default_page_id.trim() && !isValidDefaultPageId(draft.default_page_id)) {
    errors.fields.default_page_id =
      "Enter the bare numeric page id, e.g. 1234567890.";
  }

  errors.ok = Object.keys(errors.fields).length === 0;
  return errors;
}
